import {
  IconBuilding,
  IconDogGrooming,
  IconDogTraining,
  IconDogWalking,
  IconHouseVisiting,
  IconPetDayCare,
  IconPetSitting,
} from '@madpaws/design-system';

import type { ServiceTypeTimeUnit } from '@madpaws/design-system';
import type { Props as IconProps } from '@madpaws/design-system/dist/components/icons/types';
import type { ReactElement } from 'react';
import type { Chronology, WeekDays } from '~/common/types/dateTime';
import type { PetTypes } from '~/common/types/search';

export const DOM_CONTAINER_ID = '__next';
export const PHONE_INPUT_MASK = 'xxxx xxx xxx';

export const LEGAL_LINKS = [
  {
    label: 'Privacy Policy',
    href: 'https://www.madpaws.com.au/privacy-policy',
  },
  {
    label: 'Terms of Service',
    href: 'https://www.madpaws.com.au/terms-of-service',
  },
];

export const EMPTY_SERVICE_TYPE_ID = 0;
export const PET_HOSTING_SERVICE_ID = 1;
export const PET_SITTING_SERVICE_ID = 2;
export const PET_DAY_CARE_SERVICE_ID = 3;
export const HOUSE_VISITING_SERVICE_ID = 4;
export const DOG_WALKING_SERVICE_ID = 5;
export const DOG_GROOMING_SERVICE_ID = 6;
export const DOG_TRAINING_SERVICE_ID = 7;

export const SERVICE_TYPES_IDS = [
  PET_HOSTING_SERVICE_ID,
  PET_SITTING_SERVICE_ID,
  PET_DAY_CARE_SERVICE_ID,
  HOUSE_VISITING_SERVICE_ID,
  DOG_WALKING_SERVICE_ID,
  DOG_GROOMING_SERVICE_ID,
  DOG_TRAINING_SERVICE_ID,
];

export const TRAVELLING_SERVICE_TYPES_IDS = {
  pickup: 'pickUp',
  dropoff: 'dropOff',
};

export const DEFAULT_SERVICE_TYPE_ID = PET_HOSTING_SERVICE_ID;

export const OVERNIGHT_SERVICE_IDS = [PET_SITTING_SERVICE_ID, PET_HOSTING_SERVICE_ID];

export const RWB_SERVICE_IDS = [
  PET_DAY_CARE_SERVICE_ID,
  HOUSE_VISITING_SERVICE_ID,
  DOG_WALKING_SERVICE_ID,
];

export const DEFAULT_WEEK_DAYS_DATA: WeekDays = {
  sunday: false,
  monday: false,
  tuesday: false,
  wednesday: false,
  thursday: false,
  friday: false,
  saturday: false,
};

export const DEFAULT_PET_TYPES_DATA: PetTypes = {
  bird: '',
  cat: '',
  creature: '',
  giantDog: '',
  largeDog: '',
  mediumDog: '',
  puppy: '',
  smallDog: '',
};

export const DEFAULT_CHRONOLOGY_DETAILS: Chronology = {
  startDate: '',
  endDate: '',
  rwbStartDate: '',
  scheduledDates: [],
  weekDays: DEFAULT_WEEK_DAYS_DATA,
};

export const SERVICE_TYPES_SUPPORTING_RWB = [
  PET_DAY_CARE_SERVICE_ID,
  HOUSE_VISITING_SERVICE_ID,
  DOG_WALKING_SERVICE_ID,
];

export const OVERNIGHT_SERVICE_TYPE_IDS = [PET_HOSTING_SERVICE_ID, PET_SITTING_SERVICE_ID];

export const DAYTIME_SERVICE_TYPE_IDS = [
  PET_DAY_CARE_SERVICE_ID,
  HOUSE_VISITING_SERVICE_ID,
  DOG_WALKING_SERVICE_ID,
  DOG_GROOMING_SERVICE_ID,
  DOG_TRAINING_SERVICE_ID,
];

export type Icon = ({ size }: IconProps) => ReactElement;

export type ServiceTypeDetails = {
  icon: Icon;
  id: number;
  label: string;
  name: string;
  slug: string;
  timeUnit: ServiceTypeTimeUnit;
};

export const SERVICE_TYPES_NAMES = {
  PET_HOSTING: 'Pet Hosting',
  PET_SITTING: 'Pet Sitting',
  PET_DAY_CARE: 'Pet Day Care',
  HOUSE_VISITING: 'House Visiting',
  DOG_WALKING: 'Dog Walking',
  DOG_GROOMING: 'Dog Grooming',
  DOG_TRAINING: 'Dog Training',
};

export const SERVICE_TYPES: ServiceTypeDetails[] = [
  {
    icon: IconPetSitting,
    id: PET_HOSTING_SERVICE_ID,
    slug: 'petHosting',
    name: SERVICE_TYPES_NAMES.PET_HOSTING,
    label: 'Pet Hosting',
    timeUnit: 'night',
  },
  {
    icon: IconPetSitting,
    id: PET_SITTING_SERVICE_ID,
    slug: 'petSitting',
    name: SERVICE_TYPES_NAMES.PET_SITTING,
    label: 'Pet Sitting',
    timeUnit: 'night',
  },
  {
    icon: IconPetDayCare,
    id: PET_DAY_CARE_SERVICE_ID,
    slug: 'petDayCare',
    name: SERVICE_TYPES_NAMES.PET_DAY_CARE,
    label: 'Pet Day Care',
    timeUnit: 'day',
  },
  {
    icon: IconHouseVisiting,
    id: HOUSE_VISITING_SERVICE_ID,
    slug: 'houseVisiting',
    name: SERVICE_TYPES_NAMES.HOUSE_VISITING,
    label: 'House Visiting',
    timeUnit: 'visit',
  },
  {
    icon: IconDogWalking,
    id: DOG_WALKING_SERVICE_ID,
    slug: 'dogWalking',
    name: SERVICE_TYPES_NAMES.DOG_WALKING,
    label: 'Dog Walking',
    timeUnit: 'walk',
  },
  {
    icon: IconDogGrooming,
    id: DOG_GROOMING_SERVICE_ID,
    slug: 'grooming',
    name: SERVICE_TYPES_NAMES.DOG_GROOMING,
    label: 'Dog Grooming',
    timeUnit: 'session',
  },
  {
    icon: IconDogTraining,
    id: DOG_TRAINING_SERVICE_ID,
    slug: 'training',
    name: SERVICE_TYPES_NAMES.DOG_TRAINING,
    label: 'Dog Training',
    timeUnit: 'session',
  },
];

export const TRAVELLING_SERVICE_TYPES = [
  {
    icon: IconBuilding,
    id: TRAVELLING_SERVICE_TYPES_IDS.pickup,
    label: 'Pick Up',
    timeUnit: 'journey',
  },
  {
    icon: IconBuilding,
    id: TRAVELLING_SERVICE_TYPES_IDS.dropoff,
    label: 'Drop Off',
    timeUnit: 'journey',
  },
];

export const SERVICE_TYPES_TIME_UNITS: {
  [key: number]: string;
} = SERVICE_TYPES.reduce((obj, service) => ({ ...obj, [service.id]: service.timeUnit }), {});

export const SERVICE_TYPE_SLUGS_TO_SERVICE_TYPE_IDS: {
  [key: string]: number;
} = SERVICE_TYPES.reduce((obj, service) => ({ ...obj, [service.slug]: service.id }), {});

export const SERVICE_TYPE_IDS_TO_SERVICE_TYPE_SLUGS: {
  [key: number]: string;
} = SERVICE_TYPES.reduce((obj, service) => ({ ...obj, [service.id]: service.slug }), {});

export const DOG_SMALL_PET_TYPE_ID = 1;
export const DOG_MEDIUM_PET_TYPE_ID = 2;
export const DOG_LARGE_PET_TYPE_ID = 3;
export const DOG_XLARGE_PET_TYPE_ID = 4;
export const DOG_PUPPY_PET_TYPE_ID = 5;
export const CAT_PET_TYPE_ID = 6;
export const BIRD_PET_TYPE_ID = 7;
export const CRITTER_PET_TYPE_ID = 8;

export const PET_TYPE_IDS = [
  DOG_SMALL_PET_TYPE_ID,
  DOG_MEDIUM_PET_TYPE_ID,
  DOG_LARGE_PET_TYPE_ID,
  DOG_XLARGE_PET_TYPE_ID,
  DOG_PUPPY_PET_TYPE_ID,
  CAT_PET_TYPE_ID,
  BIRD_PET_TYPE_ID,
  CRITTER_PET_TYPE_ID,
];

export const PET_TYPE_LABELS = {
  PUPPY: 'Puppy',
  SMALL_DOG: 'Small dog',
  MEDIUM_DOG: 'Medium dog',
  LARGE_DOG: 'Large dog',
  GIANT_DOG: 'Giant dog',
  CAT: 'Cat',
  RABBIT: 'Rabbit/Guinea pig',
  BIRD: 'Bird',
};

export const DOG_TYPE_LABELS = {
  SMALL_DOG: 'Small',
  MEDIUM_DOG: 'Medium',
  LARGE_DOG: 'Large',
  GIANT_DOG: 'Giant',
};

export const PET_TYPE_NAMES = {
  PUPPY: 'puppy',
  SMALL_DOG: 'smallDog',
  MEDIUM_DOG: 'mediumDog',
  LARGE_DOG: 'largeDog',
  GIANT_DOG: 'giantDog',
  CAT: 'cat',
  RABBIT: 'creature',
  BIRD: 'bird',
};

export const MAIN_PET_TYPE_LABELS = {
  PUPPY: 'Puppy',
  DOG: 'Dog',
  CAT: 'Cat',
  OTHER: 'Other',
};

export const MAIN_PET_TYPE_NAMES = {
  PUPPY: 'puppy',
  DOG: 'dog',
  CAT: 'cat',
  OTHER: 'otherPets',
};

// No description or weight range for birds, rabbits and other cats
export const PET_TYPE_INFO = {
  PUPPY: 'Under 6 months',
  SMALL_DOG: '0-10 kg',
  MEDIUM_DOG: '11-20 kg',
  LARGE_DOG: '21-40 kg',
  GIANT_DOG: '41+ kg',
};

export const DOG_TYPE_INFO = {
  SMALL_DOG: '1-10 kg',
  MEDIUM_DOG: '11-20 kg',
  LARGE_DOG: '21-40 kg',
  GIANT_DOG: '41+ kg',
};

export const PET_TYPES = [
  {
    id: DOG_PUPPY_PET_TYPE_ID,
    label: PET_TYPE_LABELS.PUPPY,
    name: PET_TYPE_NAMES.PUPPY,
    labelPlural: 'Puppies',
    description: PET_TYPE_INFO.PUPPY,
  },
  {
    id: DOG_SMALL_PET_TYPE_ID,
    label: PET_TYPE_LABELS.SMALL_DOG,
    weightRange: PET_TYPE_INFO.SMALL_DOG,
    name: PET_TYPE_NAMES.SMALL_DOG,
    labelPlural: 'Small dogs',
  },
  {
    id: DOG_MEDIUM_PET_TYPE_ID,
    label: PET_TYPE_LABELS.MEDIUM_DOG,
    weightRange: PET_TYPE_INFO.MEDIUM_DOG,
    name: PET_TYPE_NAMES.MEDIUM_DOG,
    labelPlural: 'Medium dogs',
  },
  {
    id: DOG_LARGE_PET_TYPE_ID,
    label: PET_TYPE_LABELS.LARGE_DOG,
    weightRange: PET_TYPE_INFO.LARGE_DOG,
    name: PET_TYPE_NAMES.LARGE_DOG,
    labelPlural: 'Large dogs',
  },
  {
    id: DOG_XLARGE_PET_TYPE_ID,
    label: PET_TYPE_LABELS.GIANT_DOG,
    weightRange: PET_TYPE_INFO.GIANT_DOG,
    name: PET_TYPE_NAMES.GIANT_DOG,
    labelPlural: 'Giant dogs',
  },
  {
    id: CAT_PET_TYPE_ID,
    label: PET_TYPE_LABELS.CAT,
    name: PET_TYPE_NAMES.CAT,
    labelPlural: 'Cats',
  },
  {
    id: CRITTER_PET_TYPE_ID,
    label: PET_TYPE_LABELS.RABBIT,
    name: PET_TYPE_NAMES.RABBIT,
    labelPlural: 'Rabbits/Guinea pigs',
  },
  {
    id: BIRD_PET_TYPE_ID,
    label: PET_TYPE_LABELS.BIRD,
    name: PET_TYPE_NAMES.BIRD,
    labelPlural: 'Birds',
  },
];

export const PET_TYPE_NAMES_TO_PET_TYPE_IDS: {
  [key: string]: number;
} = PET_TYPES.reduce((obj, pet) => ({ ...obj, [pet.name]: pet.id }), {});

export const PET_TYPE_IDS_TO_PET_TYPE_NAMES: {
  [key: number]: string;
} = PET_TYPES.reduce((obj, pet) => ({ ...obj, [pet.id]: pet.name }), {});

export const FORM_IDS = {
  SOCIAL_SIGN_UP_FORM: 'SOCIAL_SIGN_UP_FORM',
  SIGN_UP_FORM: 'SIGN_UP_FORM',
};

export const SITTER_PROFILE_PREFIX = 'petsitter';

export const BOOKING_TYPE_MARKETPLACE = 'marketplace';
export const BOOKING_TYPE_RWB = 'rwb';
export const BOOKING_TYPE_RWB_FIRST = 'rwb_first';
export const BOOKING_TYPE_RWB_ROLLING = 'rwb_rolling';
export const BOOKING_TYPES = [
  BOOKING_TYPE_MARKETPLACE,
  BOOKING_TYPE_RWB,
  BOOKING_TYPE_RWB_FIRST,
  BOOKING_TYPE_RWB_ROLLING,
];
export const BOOKING_TYPES_RWBS = [
  BOOKING_TYPE_RWB,
  BOOKING_TYPE_RWB_FIRST,
  BOOKING_TYPE_RWB_ROLLING,
];

export const WEEK_DAYS = [
  {
    label: 'Monday',
    name: 'monday',
    value: 'monday',
    displayValue: 'Mon',
  },
  {
    label: 'Tuesday',
    name: 'tuesday',
    value: 'tuesday',
    displayValue: 'Tue',
  },
  {
    label: 'Wednesday',
    name: 'wednesday',
    value: 'wednesday',
    displayValue: 'Wed',
  },
  {
    label: 'Thursday',
    name: 'thursday',
    value: 'thursday',
    displayValue: 'Thu',
  },
  {
    label: 'Friday',
    name: 'friday',
    value: 'friday',
    displayValue: 'Fri',
  },
  {
    label: 'Saturday',
    name: 'saturday',
    value: 'saturday',
    displayValue: 'Sat',
  },
  {
    label: 'Sunday',
    name: 'sunday',
    value: 'sunday',
    displayValue: 'Sun',
  },
];

export const EMPTY_PET_AVATAR_LINK =
  'https://res.cloudinary.com/madpaws/image/upload/c_limit,f_auto,fl_progressive,h_600,q_auto,w_300/v1/img/no-image.png.jpg';

export const STATES = [
  { label: 'New South Wales', value: 'NSW' },
  { label: 'Victoria', value: 'VIC' },
  { label: 'Queensland', value: 'QLD' },
  { label: 'Western Australia', value: 'WA' },
  { label: 'South Australia', value: 'SA' },
  { label: 'Tasmania', value: 'TAS' },
  { label: 'Australian Capital Territory', value: 'ACT' },
  { label: 'Northern Territory', value: 'NT' },
];

export const STATE_ABBREVIATIONS = STATES.map((state) => state.value);

export const BOOKING_STATUS_PENDING = 1;
export const BOOKING_STATUS_SITTER_DECLINED = 2;
export const BOOKING_STATUS_CUSTOMER_CANCELLED = 3;
export const BOOKING_STATUS_SITTER_CANCELLED = 4;
export const BOOKING_STATUS_EXPIRED = 5;
export const BOOKING_STATUS_APPROVED = 6;
export const BOOKING_STATUS_ONGOING = 7;
export const BOOKING_STATUS_COMPLETED = 8;
export const BOOKING_STATUS_PAYMENT_PROCESSING = 9;
export const BOOKING_STATUS_CANCELLATION_REQUESTED = 10;

export const UNICODE_VARIANTS: { [key: string]: string } = {
  ᴍ: 'm',
  ᴀ: 'a',
  ᴅ: 'd',
  ᴘ: 'p',
  ᴡ: 'w',
  s: 's',
  ᵐ: 'm',
  ᵃ: 'a',
  ᵈ: 'd',
  ᵖ: 'p',
  ʷ: 'w',
  ˢ: 's',
  ₘ: 'm',
  ₐ: 'a',
  ₚ: 'p',
  ₛ: 's',
  '𝒎': 'm',
  '𝒶': 'a',
  '𝒹': 'd',
  '𝓅': 'p',
  '𝓌': 'w',
  '𝓈': 's',
  '𝓶': 'm',
  '𝓪': 'a',
  '𝓭': 'd',
  '𝓹': 'p',
  '𝔀': 'w',
  '𝔰': 's',
  '𝐦': 'm',
  '𝐚': 'a',
  '𝐝': 'd',
  '𝐩': 'p',
  '𝐰': 'w',
  '𝐬': 's',
  '𝑚': 'm',
  '𝑎': 'a',
  '𝑑': 'd',
  '𝑝': 'p',
  '𝑤': 'w',
  '𝑠': 's',
  '𝗆': 'm',
  '𝖺': 'a',
  '𝖽': 'd',
  '𝗉': 'p',
  '𝗐': 'w',
  '𝗌': 's',
  '𝔪': 'm',
  '𝔞': 'a',
  '𝔡': 'd',
  '𝔭': 'p',
  '𝔴': 'w',
  '𝗠': 'm',
  '𝗔': 'a',
  '𝗗': 'd',
  '𝗣': 'p',
  '𝗪': 'w',
  '𝗦': 's',
  ä: 'a',
  ö: 'o',
  ü: 'u',
  é: 'e',
  è: 'e',
  ê: 'e',
  á: 'a',
  à: 'a',
  â: 'a',
  ç: 'c',
  ñ: 'n',
  ï: 'i',
};
